import { AppBar, Toolbar, Box, Container, Button, Menu, MenuItem, Typography } from '@mui/material'
import { Home, ArrowDropDown, Inventory, PointOfSale, People, Settings, Block, AccountCircle, Logout, Public } from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

function SubHeader() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [lightspeedOpen, setLightspeedOpen] = useState(false)
  const [skimmerOpen, setSkimmerOpen] = useState(false)
  const [lightspeedAnchorEl, setLightspeedAnchorEl] = useState<null | HTMLElement>(null)
  const [skimmerAnchorEl, setSkimmerAnchorEl] = useState<null | HTMLElement>(null)
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null)

  const handleNavigate = (path: string) => {
    navigate(path)
    setLightspeedOpen(false)
    setSkimmerOpen(false)
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setUserAnchorEl(null)
  }

  const handleLogout = async () => {
    handleUserMenuClose()
    await logout()
  }

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        bgcolor: 'info.light',
        boxShadow: 1,
        top: '80px', // Position below the main navigation header
        zIndex: (theme) => theme.zIndex.appBar - 1
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Left - Menu */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                color="inherit"
                startIcon={<Home />}
                onClick={() => navigate('/')}
                sx={{ 
                  color: 'white',
                  bgcolor: location.pathname === '/' ? 'rgba(255, 255, 255, 0.1)' : 'transparent' 
                }}
              >
                Home
              </Button>
              
              {/* Lightspeed Menu Container */}
              <Box 
                sx={{ position: 'relative' }}
                onMouseEnter={(e) => {
                  setLightspeedOpen(true)
                  setLightspeedAnchorEl(e.currentTarget.firstElementChild as HTMLElement)
                }}
                onMouseLeave={() => setLightspeedOpen(false)}
              >
                <Button
                  color="inherit"
                  endIcon={<ArrowDropDown />}
                  sx={{ 
                    color: 'white',
                    bgcolor: location.pathname.includes('/lightspeed') ? 'rgba(255, 255, 255, 0.1)' : 'transparent' 
                  }}
                >
                  Lightspeed
                </Button>
                <Menu
                  anchorEl={lightspeedAnchorEl}
                  open={lightspeedOpen}
                  onClose={() => setLightspeedOpen(false)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      style: {
                        marginTop: '0px', // Remove default gap
                        pointerEvents: 'auto'
                      }
                    }
                  }}
                  MenuListProps={{
                    style: { pointerEvents: 'auto' }
                  }}
                >
                  <MenuItem 
                    onClick={() => handleNavigate('/lightspeed/products')}
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      minHeight: 48,
                      '&:hover': {
                        bgcolor: 'rgba(0, 127, 152, 0.08)'
                      }
                    }}
                  >
                    <Inventory sx={{ mr: 2, color: '#007f98', fontSize: '1.2rem' }} />
                    Products Export
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigate('/lightspeed/sales')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      minHeight: 48,
                      '&:hover': {
                        bgcolor: 'rgba(0, 127, 152, 0.08)'
                      }
                    }}
                  >
                    <PointOfSale sx={{ mr: 2, color: '#007f98', fontSize: '1.2rem' }} />
                    Sales Export
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigate('/lightspeed/customers')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      minHeight: 48,
                      '&:hover': {
                        bgcolor: 'rgba(0, 127, 152, 0.08)'
                      }
                    }}
                  >
                    <People sx={{ mr: 2, color: '#007f98', fontSize: '1.2rem' }} />
                    Customers Export
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigate('/lightspeed/retailer-setup')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      minHeight: 48,
                      '&:hover': {
                        bgcolor: 'rgba(0, 127, 152, 0.08)'
                      }
                    }}
                  >
                    <Settings sx={{ mr: 2, color: '#007f98', fontSize: '1.2rem' }} />
                    Retailer Setup Export
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigate('/lightspeed/bulk-sales')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      minHeight: 48,
                      '&:hover': {
                        bgcolor: 'rgba(0, 127, 152, 0.08)'
                      }
                    }}
                  >
                    <Public sx={{ mr: 2, color: '#007f98', fontSize: '1.2rem' }} />
                    Bulk Sales Export
                  </MenuItem>
                </Menu>
              </Box>

              {/* Skimmer Menu Container */}
              <Box 
                sx={{ position: 'relative' }}
                onMouseEnter={(e) => {
                  setSkimmerOpen(true)
                  setSkimmerAnchorEl(e.currentTarget.firstElementChild as HTMLElement)
                }}
                onMouseLeave={() => setSkimmerOpen(false)}
              >
                <Button
                  color="inherit"
                  endIcon={<ArrowDropDown />}
                  sx={{ 
                    color: 'white',
                    bgcolor: location.pathname.includes('/skimmer') ? 'rgba(255, 255, 255, 0.1)' : 'transparent' 
                  }}
                >
                  Skimmer
                </Button>
                <Menu
                  anchorEl={skimmerAnchorEl}
                  open={skimmerOpen}
                  onClose={() => setSkimmerOpen(false)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      style: {
                        marginTop: '0px', // Remove default gap
                        pointerEvents: 'auto'
                      }
                    }
                  }}
                  MenuListProps={{
                    style: { pointerEvents: 'auto' }
                  }}
                >
                  <MenuItem 
                    disabled
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      minHeight: 48
                    }}
                  >
                    <Block sx={{ mr: 2, color: 'text.disabled', fontSize: '1.2rem' }} />
                    Not Available
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Right - User Menu */}
            <Box>
              <Button
                onClick={handleUserMenuOpen}
                startIcon={<AccountCircle />}
                sx={{
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {user?.name || user?.email || 'User'}
              </Button>
              <Menu
                anchorEl={userAnchorEl}
                open={Boolean(userAnchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="textSecondary">
                    {user?.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout fontSize="small" sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default SubHeader